/**
 * Utility functions for displaying AI player names with engine status prefixes
 */

export type AIEngineStatus = 'active' | 'fallback' | 'unknown';

/**
 * Get display name for an AI player with appropriate prefix
 * - AE- prefix for AI Engine (external AI)
 * - FL- prefix for Fallback Logic (built-in AI)
 * - No prefix for unknown status or human players
 */
export function getAIPlayerDisplayName(
  playerName: string,
  isAI: boolean,
  aiEngineStatus?: AIEngineStatus
): string {
  if (!isAI || !aiEngineStatus || aiEngineStatus === 'unknown') {
    return playerName;
  }

  // Remove existing prefix if present (in case of duplicate calls)
  const cleanName = playerName.replace(/^(AE-|FL-)/, '');

  switch (aiEngineStatus) {
    case 'active':
      return `AE-${cleanName}`; // AI Engine
    case 'fallback':
      return `FL-${cleanName}`; // Fallback Logic
    default:
      return cleanName;
  }
}

/**
 * Get a color indicator for the AI engine status
 */
export function getAIEngineStatusColor(aiEngineStatus?: AIEngineStatus): string {
  switch (aiEngineStatus) {
    case 'active':
      return '#10b981'; // green-500
    case 'fallback':
      return '#f59e0b'; // amber-500
    default:
      return '#6b7280'; // gray-500
  }
}

/**
 * Get emoji indicator for AI engine status
 */
export function getAIEngineStatusEmoji(aiEngineStatus?: AIEngineStatus): string {
  switch (aiEngineStatus) {
    case 'active':
      return '⚡'; // Lightning for AI Engine
    case 'fallback':
      return '🔧'; // Wrench for Fallback Logic
    default:
      return '🤖'; // Robot for generic AI
  }
}

/**
 * Get tooltip text for AI engine status
 */
export function getAIEngineStatusTooltip(aiEngineStatus?: AIEngineStatus): string {
  switch (aiEngineStatus) {
    case 'active':
      return 'Using external AI Engine (advanced decision-making)';
    case 'fallback':
      return 'Using built-in Fallback Logic (standard AI)';
    default:
      return 'AI player';
  }
}
